// OMNI Universal Modal Component using Chakra UI v3
// Provides consistent modal styling based on omni_standard_css.css

import { forwardRef } from 'react';
import { Dialog as ChakraDialog, Portal, chakra } from '@chakra-ui/react';
import { CloseButton } from '../close-button';
import { omniModalRecipe } from '../../../theme/recipes/omni-modal';

// Create a chakra-enhanced Dialog with the omni modal recipe
const StyledDialogRoot = chakra(ChakraDialog.Root, omniModalRecipe);

export interface OmniModalProps {
  children: React.ReactNode;
  open: boolean;
  onOpenChange?: (details: { open: boolean }) => void;

  // Modal configuration
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'brand' | 'neutral';
  closeOnInteractOutside?: boolean;
  closeOnEscape?: boolean;

  // Standard HTML/React props
  className?: string;
  style?: React.CSSProperties;
}

export interface OmniModalContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
}

export interface OmniModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export interface OmniModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export interface OmniModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

// Main Modal Component
export const OmniModal = forwardRef<HTMLDivElement, OmniModalProps>(
  (
    {
      children,
      open,
      onOpenChange,
      size = 'md',
      variant = 'default',
      closeOnInteractOutside = true,
      closeOnEscape = true,
      ...props
    },
    ref,
  ) => {
    return (
      <StyledDialogRoot
        open={open}
        onOpenChange={onOpenChange}
        closeOnInteractOutside={closeOnInteractOutside}
        closeOnEscape={closeOnEscape}
        variant={variant}
        size={size}
        ref={ref}
        {...props}
      >
        {children}
      </StyledDialogRoot>
    );
  },
);

// Modal Content Component with OMNI styling
export const OmniModalContent = forwardRef<
  HTMLDivElement,
  OmniModalContentProps
>(
  (
    { children, portalled = true, portalRef, backdrop = true, ...props },
    ref,
  ) => {
    const content = (
      <>
        {backdrop && <ChakraDialog.Backdrop />}
        <ChakraDialog.Positioner>
          <ChakraDialog.Content ref={ref} {...props} asChild={false}>
            {children}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </>
    );

    if (portalled) {
      return <Portal container={portalRef}>{content}</Portal>;
    }

    return content;
  },
);

// Modal Header Component
export const OmniModalHeader = forwardRef<HTMLDivElement, OmniModalHeaderProps>(
  ({ children, ...props }, ref) => {
    return (
      <ChakraDialog.Header ref={ref} {...props}>
        {children}
      </ChakraDialog.Header>
    );
  },
);

// Modal Body Component
export const OmniModalBody = forwardRef<HTMLDivElement, OmniModalBodyProps>(
  ({ children, ...props }, ref) => {
    return (
      <ChakraDialog.Body ref={ref} {...props}>
        {children}
      </ChakraDialog.Body>
    );
  },
);

// Modal Footer Component
export const OmniModalFooter = forwardRef<HTMLDivElement, OmniModalFooterProps>(
  ({ children, ...props }, ref) => {
    return (
      <ChakraDialog.Footer ref={ref} {...props}>
        {children}
      </ChakraDialog.Footer>
    );
  },
);

// Modal Close Button Component
export const OmniModalCloseTrigger = forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>((props, ref) => {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="3"
      insetEnd="3"
      ref={ref}
      {...props}
      asChild
    >
      <CloseButton size="sm" />
    </ChakraDialog.CloseTrigger>
  );
});

// Set display names for debugging
OmniModal.displayName = 'OmniModal';
OmniModalContent.displayName = 'OmniModalContent';
OmniModalHeader.displayName = 'OmniModalHeader';
OmniModalBody.displayName = 'OmniModalBody';
OmniModalFooter.displayName = 'OmniModalFooter';
OmniModalCloseTrigger.displayName = 'OmniModalCloseTrigger';
